import _ from 'lodash';
import moment from 'moment';
import { Subject } from 'rxjs';
import { Injectable } from '@angular/core';

import { GetFeatureBlackoutDatesResult } from '../../../types/get-feature-blackout-dates-result.interface';
import { AvailableSiteAdvertDaysResult } from '../../../types/available-site-adverts-day-result.interface';

import { CoreModule } from '../../core.module';
import { Book } from '../../../classes/book';

import { FeaturedBooksService } from './featured-books.service';
import { SiteAdvertDaysService } from './site-advert-days.service';
import { UserService } from './user.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
	providedIn: CoreModule,
})
export class PromoWizardService {
	public bookPromoPrice: number;

	public _selectedBook: Book;

	public genres: Array<any> = [];

	public startDate: Date;

	public endDate: Date;

	public blackoutDateList: any[] = [];

	public adverts: Array<any> = [];

	public currentBlackoutListASIN = '';

	public inventory: any[];

	public termsAccepted = false;

	private emitChangeSource = new Subject<any>();

	private changeEmitted$ = this.emitChangeSource.asObservable();

	private _kindleCountdownDeal: boolean;

	private _kindleUnlimited: boolean;

	private _availableAdverts: any[] = [];

	private _state = 'empty';

	private _persistTheseProps = [
		'bookPromoPrice',
		'selectedBook',
		'genres',
		'startDate',
		'endDate',
		'blackoutDateList',
		'currentBlackoutListASIN',
		'inventory',
		'kindleCountdownDeal',
		'kindleUnlimited',
		'availableAdverts',
		'state',
	];

	private _dataExpirationInMinutes = 5;

	constructor(
		private featuredBooksService: FeaturedBooksService,
		private siteAdvertDaysService: SiteAdvertDaysService,
		private userService: UserService,
		private localStorageService: LocalStorageService,
	) {
		this.setDataFromStorage();
		// @HostListener('window:beforeunload', ['$event']) Cannot be used in a service.
		window.onbeforeunload = () => {
			this.persistToStorage();
		};
	}

	set selectedBook(book) {
		this._selectedBook = book;
		this.emitChangeSource.next(book);
		this.fetchBlackoutDates();
	}

	get selectedBook() {
		return this._selectedBook;
	}

	set kindleCountdownDeal(value) {
		this._kindleCountdownDeal = value;
	}

	get kindleCountdownDeal() {
		return this._kindleCountdownDeal;
	}

	set kindleUnlimited(value) {
		this._kindleUnlimited = value;
	}

	get kindleUnlimited() {
		return this._kindleUnlimited;
	}

	set availableAdverts(value) {
		this._availableAdverts = value;
	}

	get availableAdverts() {
		return this._availableAdverts;
	}

	set state(state) {
		this._state = state;
	}

	get state() {
		return this._state;
	}

	get genreList() {
		return this.genres.join(', ');
	}

	clearData() {
		this.selectedBook = null;
		this.genres = [];
		this.bookPromoPrice = 0;
		this.startDate = null;
		this.endDate = null;
		this.blackoutDateList = [];
		this.kindleUnlimited = false;
		this.kindleCountdownDeal = false;
		this.availableAdverts = [];
		this.state = 'empty';
	}

	setDataFromStorage() {
		const promoWizardData = this.localStorageService.getJSONData('promoWizard');

		if (!promoWizardData) return;

		if (this.isExpiredOrDifferentUser(promoWizardData)) {
			this.localStorageService.removeData('promoWizard');
			return;
		}

		// Clear metadata from storage object before applying to service data.
		delete promoWizardData.exp;
		delete promoWizardData.user;

		Object.assign(this, promoWizardData);
	}

	isExpiredOrDifferentUser(promoWizardData) {
		return promoWizardData.exp < new Date().getTime() || this.userService.me.username !== promoWizardData.user;
	}

	persistToStorage() {
		const copy = _.cloneDeep(_.pick(this, this._persistTheseProps)) as any;
		copy.exp = this.getExpirationDate();
		copy.user = this.userService.me.username;

		// Dont allow loading state to be persisted.
		if (copy.state === 'loading') {
			copy.state = 'empty';
		}

		this.localStorageService.saveJSONData('promoWizard', copy);
	}

	getExpirationDate() {
		return new Date().getTime() + 1000 * 60 * this._dataExpirationInMinutes;
	}

	bookModified(book: any) {
		this.selectedBook = book;
	}

	fetchBlackoutDates() {
		if (!this.selectedBook) return;

		const { ASIN } = this.selectedBook;

		if (!ASIN || ASIN === this.currentBlackoutListASIN) return;

		this.featuredBooksService.getFeatureBlackoutDates(ASIN).subscribe((data: GetFeatureBlackoutDatesResult) => {
			this.blackoutDateList = data.blackoutDateList;
			this.currentBlackoutListASIN = ASIN;
		});
	}

	fetchAdverts() {
		const query = {
			book: this.selectedBook,
			genres: this.handleGenreTypos(),
			promoPrice: Number(this.bookPromoPrice),
			dates: {
				start: moment(this.startDate).format('MM/DD/YYYY'),
				end: moment(this.endDate).format('MM/DD/YYYY'),
			},
			memberRole: this.userService.role?._id,
		};

		return new Promise((resolve, reject) => this.siteAdvertDaysService.fetchAvailableSiteAdvertDays(query)
			.subscribe((data: AvailableSiteAdvertDaysResult) => {
				this.inventory = data.inventory;
				this.handleAdverts();
				resolve(data.inventory);
			}));
	}

	handleAdverts() {
		const { inventory } = this;

		this.availableAdverts = inventory.map((advert) => {
			advert.audience = this.capitalize(advert.audience);

			advert.disabled = false;
			advert.selected = false;

			return advert;
		}).filter((_inventory) => {
			const pubDate = moment(_inventory.date);
			const blackout = this.blackoutDateList
				.find(({ siteId, blackoutDates }) => this.isBlackedOut(_inventory.siteId, siteId, pubDate, blackoutDates));
			return !blackout;
		});

		if (this.availableAdverts.length === 0) {
			this.state = 'no-data';
			return;
		}

		this.state = 'display';
	}

	capitalize(str) {
		return str.charAt(0).toUpperCase() + str.slice(1);
	}

	isBlackedOut(inventorySiteId, blackoutSiteId, pubDate, [startDate, endDate]) {
		return inventorySiteId === blackoutSiteId && pubDate.isBetween(moment(startDate), moment(endDate));
	}

	handleGenreTypos() {
		// TODO: Temporary to handle the DB having typos in the entry.
		const { genres } = this;
		const _genres = [...genres];

		if (genres.includes('Travel')) {
			_genres.push('Adventure / Travel');
		}

		if (genres.includes('Fantasy / Paranormal')) {
			_genres.push('Fantasy/ Paranormal');
			_genres.push('Fantasy');
		}

		return _genres;
	}

	notReadyToQuery() {
		return !this.selectedBook
			|| !this.genres.length
			|| !this.bookPromoPrice
			|| !this.startDate
			|| !this.endDate;
	}
}
